import { useState, useEffect } from 'react';
import api from '../../api'
import { useAuth } from '../../authContext';

export const useTasks = () => {
  // Authentifizierung und Nutzerdaten
  const { token, role, userId } = useAuth();

  // State für Aufgaben, Kontakte und Unternehmen
  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Hilfsfunktionen ---
  // Formatierung von Datumsangaben
  const formatDate = (dateString) => {
    if (!dateString) return '–';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  // Prüfen, ob eine Aufgabe überfällig ist
  const isOverdue = (task) => {
    return (
      (task.taskStatus || task.Status) === 'TODO' &&
      task.taskDueDate &&
      new Date(task.taskDueDate) < new Date(new Date().toDateString())
    );
  };

  // Kontakte eines Unternehmens filtern
  const getContactsForCompany = (companyId) => {
    if (!companyId) return [];
    return contacts.filter((contact) => contact.companyId === companyId && contact.active);
  };

  // --- API-Aufrufe ---
  // Aufgaben, Kontakte und Unternehmen laden
  const fetchTasksWithDetails = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [tasksRes, contactsRes, companiesRes] = await Promise.all([
        api.get('/tasks/with-details', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get('/contacts', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get('/companies', {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setTasks(tasksRes.data || []);
      setContacts(contactsRes.data || []);
      setCompanies(companiesRes.data || []);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Aufgaben, Kontakte oder Unternehmen. Bitte versuche es später erneut.');
      console.error('Fehler:', err);
    } finally {
      setLoading(false);
    }
  };

  // Aufgabe hinzufügen
  const addTask = async (taskData) => {
    try {
      const response = await api.post(
        '/tasks',
        {
          Title: taskData.Title,
          Description: taskData.Description,
          ContactId: parseInt(taskData.ContactId),
          DueDate: taskData.DueDate,
          UserId: userId,
          Status: 'TODO',
          CreatedAt: new Date().toISOString(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Neue Aufgabe zur lokalen Liste hinzufügen
      const addedTask = {
        taskId: response.data.id,
        taskTitle: taskData.Title,
        taskDescription: taskData.Description,
        contactId: parseInt(taskData.ContactId),
        contactFirstName: contacts.find((c) => c.id === parseInt(taskData.ContactId))?.firstName || '',
        contactLastName: contacts.find((c) => c.id === parseInt(taskData.ContactId))?.lastName || '',
        companyName: companies.find((c) => c.id === contacts.find((ct) => ct.id === parseInt(taskData.ContactId))?.companyId)?.name || '',
        taskStatus: 'TODO',
        taskDueDate: taskData.DueDate,
        taskCreatedAt: new Date().toISOString(),
        userId: userId,
        userUsername: 'Aktueller Nutzer',
      };
      setTasks([...tasks, addedTask]);
      return addedTask;
    } catch (err) {
      console.error('Fehler beim Erstellen der Aufgabe:', err);
      throw err;
    }
  };

  // Aufgabe bearbeiten
  const editTask = async (taskId, taskData) => {
    try {
      await api.put(
        `/tasks/${taskId}`,
        {
          Id: taskId,
          Title: taskData.Title,
          Description: taskData.Description,
          ContactId: parseInt(taskData.ContactId),
          DueDate: taskData.DueDate,
          UserId: taskData.UserId,
          CreatedAt: taskData.CreatedAt,
          Status: taskData.Status,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Lokale Aufgabenliste aktualisieren
      setTasks(
        tasks.map((task) =>
          task.taskId === taskId
            ? {
              ...task,
              taskTitle: taskData.Title,
              taskDescription: taskData.Description,
              contactId: parseInt(taskData.ContactId),
              taskDueDate: taskData.DueDate,
            }
            : task
        )
      );
    } catch (err) {
      console.error('Fehler beim Bearbeiten der Aufgabe:', err);
      throw err;
    }
  };

  // Aufgabe löschen
  const deleteTask = async (taskId) => {
    try {
      await api.delete(`/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTasks(tasks.filter((task) => task.taskId !== taskId));
    } catch (err) {
      console.error('Fehler beim Löschen der Aufgabe:', err);
      throw err;
    }
  };

  // Status umschalten (TODO ↔ DONE)
  const toggleTaskStatus = async (taskId, currentStatus) => {
    if (role !== 'Admin' && currentStatus !== 'TODO') {
      throw new Error('Nur Admins können den Status von Aufgaben auf "Offen" ändern.');
    }

    try {
      const endpoint = currentStatus === 'TODO' ? `/tasks/${taskId}/done` : `/tasks/${taskId}`;
      const method = 'put';
      const data = currentStatus === 'TODO' ? {} : { Status: 'TODO' };

      await api[method](`${endpoint}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setTasks(
        tasks.map((task) =>
          task.taskId === taskId
            ? {
              ...task,
              taskStatus: currentStatus === 'TODO' ? 'DONE' : 'TODO',
              completionDate: currentStatus === 'TODO' ? new Date().toISOString() : null,
            }
            : task
        )
      );
    } catch (err) {
      console.error('Fehler beim Ändern des Status:', err);
      throw err;
    }
  };

  // Neuer Ansprechpartner für ein Unternehmen (aus der Aufgabenansicht)
  const addContactForTask = async (contactData) => {
    try {
      await api.post(
        '/contacts',
        {
          ...contactData,
          companyId: parseInt(contactData.companyId),
          active: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Kontakte neu laden
      const contactsRes = await api.get('/contacts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(contactsRes.data || []);
    } catch (err) {
      console.error('Fehler beim Erstellen des Ansprechpartners:', err);
      throw err;
    }
  };

  // Seitenleisten-Logik
  const openContactSidebar = (contactId) => {
    const contact = contacts.find((c) => c.id === contactId);
    if (contact) {
      return contact;
    }
    return null;
  };

  const openCompanySidebar = (companyId) => {
    const company = companies.find((c) => c.id === companyId);
    if (company) {
      return company;
    }
    return null;
  };

  // Initiales Laden der Daten
  useEffect(() => {
    if (token) {
      fetchTasksWithDetails();
    }
  }, [token]);

  // Rückgabe aller Funktionen und States
  return {
    // Daten
    tasks,
    contacts,
    companies,
    loading,
    error,

    // API-Funktionen
    fetchTasksWithDetails,
    addTask,
    editTask,
    deleteTask,
    toggleTaskStatus,
    addContactForTask,

    // Hilfsfunktionen
    formatDate,
    isOverdue,
    getContactsForCompany,
    openContactSidebar,
    openCompanySidebar,
  };
};

export default useTasks;